import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ConfirmDialog } from './ConfirmDialog';
import { Trash2, Plus } from 'lucide-react';
import { fmtDate } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  insurer_id: '',
  document_type: 'FC',
  letra: 'B',
  punto_venta: '00001',
  numero: '',
  issue_date: new Date().toISOString().slice(0, 10),
  service_period_from: '',
  service_period_to: '',
  due_date: '',
  cae: '',
  cae_due_date: '',
  arca_environment: 'produccion',
  description: '',
  net: 0,
  iva_rate: '21',
  iva_amount: 0,
  no_gravado: 0,
  exento: 0,
  total: 0,
  notes: '',
};

// v3.28.8 · Panel de alta de facturas emitidas en el sistema anterior (transición).
// Sólo aparece para Admin.
export function LegacyInvoiceImportPanel() {
  const [rows, setRows] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/legacy-imports`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch { setRows([]); }
  };
  const loadInsurers = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/health-insurers`, { withCredentials: true });
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || r.data.insurers || []);
      setInsurers(arr);
    } catch { setInsurers([]); }
  };

  useEffect(() => { load(); loadInsurers(); }, []);

  const setF = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const recomputeIvaOnRateChange = (rate, net) => {
    const rates = { '21': 0.21, '10.5': 0.105, '0': 0 };
    if (rate in rates) return +(net * rates[rate]).toFixed(2);
    return 0;
  };

  const totalCalc = () => {
    const n = +form.net || 0;
    const i = +form.iva_amount || 0;
    const ng = +form.no_gravado || 0;
    const ex = +form.exento || 0;
    return +(n + i + ng + ex).toFixed(2);
  };

  const requiresCae = () => {
    if (form.letra === 'A') return true;
    if (form.arca_environment) return true;
    return false;
  };

  const submit = async () => {
    if (!form.insurer_id) return toast.error('Elegí el convenio destinatario');
    if (!form.numero) return toast.error('Falta el número de comprobante');
    if (!form.description.trim()) return toast.error('Falta la descripción del concepto');
    if (!form.total || +form.total <= 0) return toast.error('El total debe ser mayor a cero');
    if (requiresCae() && (!form.cae || form.cae.length !== 14)) {
      return toast.error('Esta factura vino de ARCA: el CAE es obligatorio (14 dígitos)');
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        service_period_from: form.service_period_from || null,
        service_period_to: form.service_period_to || null,
        due_date: form.due_date || null,
        cae: form.cae || null,
        cae_due_date: form.cae_due_date || null,
        arca_environment: form.arca_environment || null,
        net: +form.net || 0,
        iva_amount: +form.iva_amount || 0,
        no_gravado: +form.no_gravado || 0,
        exento: +form.exento || 0,
        total: +form.total || 0,
      };
      const r = await axios.post(`${BACKEND_URL}/api/billing/legacy-imports`, payload, { withCredentials: true });
      toast.success(`Factura ${r.data.invoice_number} cargada`);
      setOpenForm(false); setForm(EMPTY_FORM); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la factura');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/billing/legacy-imports/${confirmDel.invoice_id}`, { withCredentials: true });
      toast.success(`${confirmDel.invoice_number} eliminada`);
      setConfirmDel(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    } finally { setDeleting(false); }
  };

  return (
    <Card data-testid="legacy-imports-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Facturas preexistentes (sistema anterior)</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Cargá acá las facturas de IOMA / PAMI (u otro convenio) que fueron emitidas en el sistema viejo
            y todavía están <b>pendientes de cobro</b>. Sirven para reflejar la deuda en la cuenta corriente
            del convenio y para emitir Notas de Crédito / Débito por ARCA usándolas como comprobante asociado.
            No impactan en el Libro IVA Ventas (ya fueron declaradas por el sistema anterior).
          </p>
        </div>
        <Button size="sm" onClick={() => { setForm(EMPTY_FORM); setOpenForm(true); }} data-testid="legacy-imports-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no cargaste ninguna factura preexistente.</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Comprobante</TableHead>
                  <TableHead>Convenio</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>CAE</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Cobrado</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.invoice_id} data-testid={`legacy-row-${r.invoice_number}`}>
                    <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                    <TableCell>{r.billed_to?.legal_name || '—'}</TableCell>
                    <TableCell>{fmtDate(r.issue_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.cae || <span className="text-muted-foreground">(sin CAE)</span>}</TableCell>
                    <TableCell className="text-right">${(r.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-right">${(r.paid_amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(r)}
                        disabled={(r.paid_amount || 0) > 0 || (r.credited_amount || 0) > 0}
                        title={(r.paid_amount || 0) > 0 ? 'No se puede eliminar: tiene cobros aplicados' : 'Eliminar'}
                        data-testid={`legacy-del-${r.invoice_number}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={openForm} onOpenChange={(v) => !saving && setOpenForm(v)}>
        <DialogContent className="max-w-3xl" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Nueva factura preexistente</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-6">
              <Label>Convenio destinatario *</Label>
              <Select value={form.insurer_id} onValueChange={(v) => setF({ insurer_id: v })}>
                <SelectTrigger data-testid="legacy-form-insurer"><SelectValue placeholder="Elegir convenio" /></SelectTrigger>
                <SelectContent>
                  {insurers.map(i => (
                    <SelectItem key={i.insurer_id} value={i.insurer_id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Label>Tipo *</Label>
              <Select value={form.document_type} onValueChange={(v) => setF({ document_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FC">Factura</SelectItem>
                  <SelectItem value="ND">Nota de Débito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Label>Letra *</Label>
              <Select value={form.letra} onValueChange={(v) => setF({ letra: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-3">
              <Label>Punto de venta *</Label>
              <Input value={form.punto_venta} onChange={(e) => setF({ punto_venta: e.target.value })} maxLength={5} data-testid="legacy-form-pv" />
            </div>
            <div className="col-span-3">
              <Label>Número *</Label>
              <Input value={form.numero} onChange={(e) => setF({ numero: e.target.value.replace(/\D/g, '') })} maxLength={8} data-testid="legacy-form-numero" />
            </div>
            <div className="col-span-3">
              <Label>Fecha de emisión *</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setF({ issue_date: e.target.value })} />
            </div>
            <div className="col-span-3">
              <Label>Fecha vto. pago</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setF({ due_date: e.target.value })} />
            </div>

            <div className="col-span-4">
              <Label>Período desde</Label>
              <Input type="date" value={form.service_period_from} onChange={(e) => setF({ service_period_from: e.target.value })} />
            </div>
            <div className="col-span-4">
              <Label>Período hasta</Label>
              <Input type="date" value={form.service_period_to} onChange={(e) => setF({ service_period_to: e.target.value })} />
            </div>
            <div className="col-span-4">
              <Label>Ambiente ARCA</Label>
              <Select value={form.arca_environment || 'none'} onValueChange={(v) => setF({ arca_environment: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="produccion">Producción</SelectItem>
                  <SelectItem value="homologacion">Homologación</SelectItem>
                  <SelectItem value="none">(No fue por ARCA — proforma vieja)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-6">
              <Label>CAE {requiresCae() ? '*' : '(opcional)'}</Label>
              <Input value={form.cae} onChange={(e) => setF({ cae: e.target.value.replace(/\D/g, '') })}
                maxLength={14} placeholder="14 dígitos" data-testid="legacy-form-cae" />
            </div>
            <div className="col-span-6">
              <Label>Vto. CAE</Label>
              <Input type="date" value={form.cae_due_date} onChange={(e) => setF({ cae_due_date: e.target.value })} />
            </div>

            <div className="col-span-12">
              <Label>Descripción del concepto *</Label>
              <Input value={form.description} onChange={(e) => setF({ description: e.target.value })}
                placeholder="Ej: Prestación diciembre 2025 — Sr. Pérez / Afiliado XXX" data-testid="legacy-form-desc" />
            </div>

            <div className="col-span-3">
              <Label>Neto gravado</Label>
              <Input type="number" step="0.01" value={form.net}
                onChange={(e) => setF({ net: e.target.value, iva_amount: recomputeIvaOnRateChange(form.iva_rate, +e.target.value) })} />
            </div>
            <div className="col-span-3">
              <Label>Alícuota IVA</Label>
              <Select value={form.iva_rate} onValueChange={(v) => setF({ iva_rate: v, iva_amount: recomputeIvaOnRateChange(v, +form.net) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="21">21%</SelectItem>
                  <SelectItem value="10.5">10.5%</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="exento">Exento</SelectItem>
                  <SelectItem value="no_gravado">No Gravado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Label>IVA</Label>
              <Input type="number" step="0.01" value={form.iva_amount} onChange={(e) => setF({ iva_amount: e.target.value })} />
            </div>
            <div className="col-span-3">
              <Label>No gravado</Label>
              <Input type="number" step="0.01" value={form.no_gravado} onChange={(e) => setF({ no_gravado: e.target.value })} />
            </div>

            <div className="col-span-3">
              <Label>Exento</Label>
              <Input type="number" step="0.01" value={form.exento} onChange={(e) => setF({ exento: e.target.value })} />
            </div>
            <div className="col-span-6"></div>
            <div className="col-span-3">
              <Label className="font-semibold">Total *</Label>
              <div className="flex gap-1">
                <Input type="number" step="0.01" value={form.total} onChange={(e) => setF({ total: e.target.value })}
                  className="font-semibold" data-testid="legacy-form-total" />
                <Button type="button" variant="outline" size="sm" onClick={() => setF({ total: totalCalc() })}
                  title="Calcular = Neto + IVA + No gravado + Exento">=</Button>
              </div>
            </div>

            <div className="col-span-12">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setF({ notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="legacy-form-submit-btn">
              {saving ? 'Guardando...' : 'Cargar factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title={`Eliminar ${confirmDel?.invoice_number}`}
        description="Se borrará la factura preexistente y su movimiento en la cta. cte. del convenio. Esta acción no puede deshacerse."
        confirmText={deleting ? 'Eliminando...' : 'Eliminar'}
        onConfirm={doDelete}
        variant="destructive"
      />
    </Card>
  );
}
